import { apiService } from './api';
import type { User, LoginSession, AdminAction, AdminStats } from '../types';

class FirestoreService {
  // Users
  async getUsers(): Promise<User[]> {
    return apiService.getUsers();
  }

  async getUser(userId: string): Promise<User | null> {
    try {
      return await apiService.getUser(userId);
    } catch {
      return null;
    }
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    await apiService.updateUser(userId, updates);
  }

  async deleteUser(userId: string): Promise<void> {
    await apiService.deleteUser(userId);
  }

  // Login Sessions
  async getLoginSessions(): Promise<LoginSession[]> {
    return apiService.getLoginSessions();
  }

  // Admin Actions
  async getAdminActions(): Promise<AdminAction[]> {
    try {
      return await apiService.getAdminActions();
    } catch {
      // admin_actions may be empty on a fresh deployment — return [] instead of throwing
      return [];
    }
  }

  async createAdminAction(action: Omit<AdminAction, 'id' | 'createdAt' | 'adminId' | 'adminEmail'>): Promise<AdminAction> {
    return apiService.createAdminAction(action);
  }

  async updateAdminAction(actionId: string, updates: Partial<AdminAction>): Promise<void> {
    await apiService.updateAdminAction(actionId, updates);
  }

  // Statistics — single API call; aggregation is done server-side
  async getStats(): Promise<AdminStats> {
    return apiService.getStats();
  }

  // User Management Actions
  async promoteUser(userId: string, tier: 'free' | 'basic' | 'premium' | 'enterprise', duration: number): Promise<User> {
    return apiService.promoteUser(userId, tier, duration);
  }

  async issueRefund(userId: string, amount: number, reason: string): Promise<AdminAction> {
    return apiService.issueRefund(userId, amount, reason);
  }

  async suspendUser(userId: string, reason: string): Promise<User> {
    return apiService.suspendUser(userId, reason);
  }

  async activateUser(userId: string): Promise<User> {
    return apiService.activateUser(userId);
  }
}

export const firestoreService = new FirestoreService();
